/**
 * Schedule Detail Modal - full detail view for a PO Line Schedule.
 *
 * Mirrors the structure of POLineDetailModal with:
 * - Header with schedule info, hold toggle, and close button
 * - Details tab with schedule info grid
 * - Change History tab with changelog timeline
 * - Inline workflow action modals (accept, decline, propose date/qty, hold, release)
 */
import { useEffect, useState, useRef } from 'react';
import { X, Loader2, PauseCircle, Clock, Package, FileText, ShieldCheck } from 'lucide-react';
import { purchasingAPI } from '../../lib/api';
import type { POLineSchedule } from '../../lib/api/types/purchasing';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import Changelog, { ChangelogEntry } from '../common/Changelog';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import ScheduleDetailActions from './ScheduleDetailActions';
import ScheduleGRNTab from './ScheduleGRNTab';
import CommunicationLog from './CommunicationLog';
import { ScheduleDetailFormContent } from './ScheduleDetailFormContent';
import OtifOverrideDialog from './OtifOverrideDialog';
import { isCollabClosed } from './tabs/schedule-lines/types';

interface ScheduleDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  scheduleId: number;
  onUpdate?: () => void;
}

/** Acknowledge status display config. */
const ACK_BADGE: Record<string, { label: string; cls: string }> = {
  new: { label: 'New', cls: 'bg-yellow-100 text-yellow-800' },
  // Accepted displays as WIP — see ACK_STATUS_CONFIG in schedule-lines/types.ts
  accepted: { label: 'WIP', cls: 'bg-green-100 text-green-800' },
  declined: { label: 'Declined', cls: 'bg-red-100 text-red-800' },
  change_proposed_supplier: { label: 'Change Proposed (Supplier)', cls: 'bg-blue-100 text-blue-800' },
  change_proposed_customer: { label: 'Change Proposed (Customer)', cls: 'bg-purple-100 text-purple-800' },
  change_declined: { label: 'Change Declined', cls: 'bg-red-100 text-red-800' },
  wip: { label: 'WIP', cls: 'bg-cyan-100 text-cyan-800' },
  despatched: { label: 'Dispatched', cls: 'bg-indigo-100 text-indigo-800' },
  queried: { label: 'Queried', cls: 'bg-amber-100 text-amber-800' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-700' },
  closed: { label: 'Closed', cls: 'bg-gray-100 text-gray-700' },
};

/** Writeback status display config. */
const WB_BADGE: Record<string, { label: string; cls: string }> = {
  accepted: { label: 'WB: Accepted', cls: 'bg-green-100 text-green-800' },
  declined: { label: 'WB: Declined', cls: 'bg-red-100 text-red-800' },
  change_odd: { label: 'WB: Change ODD', cls: 'bg-orange-100 text-orange-800' },
  change_crd: { label: 'WB: Change CRD', cls: 'bg-orange-100 text-orange-800' },
  accepted_unit_price: { label: 'WB: Price Accepted', cls: 'bg-blue-100 text-blue-800' },
  accepted_qty: { label: 'WB: Qty Accepted', cls: 'bg-blue-100 text-blue-800' },
};

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  scheduled: { label: 'Scheduled', cls: 'bg-blue-100 text-blue-800' },
  partially_shipped: { label: 'Partially Received', cls: 'bg-yellow-100 text-yellow-800' },
  shipped: { label: 'Received', cls: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-700' },
};

export default function ScheduleDetailModal({
  isOpen,
  onClose,
  scheduleId,
  onUpdate,
}: ScheduleDetailModalProps) {
  const { user } = useAuth();
  const { formatDate: contextFormatDate } = useFormatters();

  const [schedule, setSchedule] = useState<POLineSchedule | null>(null);
  const [changelogs, setChangelogs] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Action modal state
  const [activeAction, setActiveAction] = useState<string | null>(null);
  // Staff-only manual OTIF override (same population as GRN Repair).
  const canOtifOverride = !!user?.is_superuser || !!user?.is_staff;
  const [otifOverrideOpen, setOtifOverrideOpen] = useState(false);
  // Post-override refetches must bypass the 60s Cache-Control browser cache
  // or the stale verdict/changelog replays. Ref so it never triggers renders.
  const cacheBustRef = useRef<number | null>(null);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return contextFormatDate(dateString);
  };

  const transformChangelogs = (results: Array<{
    id: number;
    change_date: string;
    changed_by_details?: { full_name?: string };
    field_changed: string;
    old_value: string;
    new_value: string;
    reason?: string;
    change_type?: string;
    event_type?: string;
  }>): ChangelogEntry[] => {
    return results.map((log) => ({
      id: log.id,
      change_date: log.change_date,
      changed_by: log.changed_by_details?.full_name || 'System',
      field_changed: log.field_changed,
      old_value: log.old_value,
      new_value: log.new_value,
      reason: log.reason,
      change_type: log.change_type,
      event_type: log.event_type,
    }));
  };

  const fetchDetails = async (silent = false) => {
    try {
      if (!silent) { setLoading(true); setError(null); }
      const bust = cacheBustRef.current ? { _t: cacheBustRef.current } : {};
      const scheduleRes = await purchasingAPI.getSchedule(scheduleId, bust);
      const poLineId = scheduleRes.data.po_line_id;
      const [changelogRes, grnSummaryRes] = await Promise.all([
        purchasingAPI.getChangelogs({ schedule: scheduleId, ordering: '-change_date', ...bust }),
        poLineId
          ? purchasingAPI.getLineGRNSummary(poLineId).catch(() => ({ data: { grns: [] as Array<{ id: number; grn_number: string; receipt_date?: string; supplier_name?: string; allocations?: Array<{ po_line_schedule: number; allocated_qty: string }> }> } }))
          : Promise.resolve({ data: { grns: [] as Array<{ id: number; grn_number: string; receipt_date?: string; supplier_name?: string; allocations?: Array<{ po_line_schedule: number; allocated_qty: string }> }> } }),
      ]);
      setSchedule(scheduleRes.data);
      // Synthesise a richer audit history for the schedule line:
      //   1. "Schedule Created" at the bottom (earliest event)
      //   2. One "Receipt" entry per GRN allocation, chronological,
      //      with the running Received/Balance totals so the reader
      //      can watch the schedule fill up delivery-by-delivery.
      //   3. Merged with the real field-change log and sorted desc.
      const scheduleQty = Number(scheduleRes.data.schedule_qty || 0);
      type SyntheticInput = {
        date: string;
        grn_id: number;
        grn_number: string;
        supplier_name: string;
        allocated: number;
      };
      const events: SyntheticInput[] = [];
      for (const g of grnSummaryRes.data.grns || []) {
        const allocs = (g.allocations || []).filter(a => a.po_line_schedule === scheduleId);
        for (const a of allocs) {
          events.push({
            date: g.receipt_date || '',
            grn_id: g.id,
            grn_number: g.grn_number,
            supplier_name: g.supplier_name || 'Goods Receipt',
            allocated: Number(a.allocated_qty || 0),
          });
        }
      }
      events.sort((a, b) => (a.date || '').localeCompare(b.date || ''));

      const synthetic: ChangelogEntry[] = [];
      let running = 0;
      for (const ev of events) {
        running += ev.allocated;
        const balance = Math.max(0, scheduleQty - running);
        synthetic.push({
          id: -1 * (ev.grn_id * 10000 + Math.round(ev.allocated * 1000)),
          change_date: ev.date,
          changed_by: ev.supplier_name,
          field_changed: `Received ${ev.allocated.toFixed(3)}`,
          old_value: '',
          new_value: (() => {
            const pctFilled = scheduleQty > 0 ? Math.round((running / scheduleQty) * 1000) / 10 : 0;
            const pctRemaining = Math.max(0, Math.round((100 - pctFilled) * 10) / 10);
            return (
              `GRN ${ev.grn_number}\n` +
              `${running.toFixed(3)} of ${scheduleQty.toFixed(3)} filled (${pctFilled.toFixed(1)}%)\n` +
              `${balance.toFixed(3)} remaining (${pctRemaining.toFixed(1)}%)`
            );
          })(),
          change_type: 'receipt',
          event_type: 'grn',
        });
      }

      // Intentionally omit a synthetic "Schedule Created" event here.
      // POLineSchedule.created_at records when Helix imported the row,
      // which for a schedule-aware tenant is the cutoff date of the
      // first import — not a meaningful "business creation" event
      // (e.g. DE's schedule #1 rows were all inserted at the cutoff,
      // 2026-04-01, even though their real schedule dates span months).
      // Surfacing that timestamp was misleading, so leave it off until
      // a reliable source exists.

      const real = transformChangelogs(changelogRes.data.results);
      const combined = [...real, ...synthetic].sort((a, b) =>
        (b.change_date || '').localeCompare(a.change_date || '')
      );
      setChangelogs(combined);
    } catch (err: unknown) {
      console.error('Failed to fetch schedule details:', err);
      if (!silent) {
        const axiosError = err as { response?: { data?: { detail?: string } } };
        setError(axiosError.response?.data?.detail || 'Failed to load schedule details');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && scheduleId) fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, scheduleId]);

  const handleToggleHold = async (reason?: string) => {
    if (!schedule) return;
    try {
      let response;
      if (schedule.on_hold) {
        response = await purchasingAPI.releaseScheduleHold(schedule.id);
      } else {
        if (!reason) return;
        response = await purchasingAPI.putScheduleOnHold(schedule.id, { reason });
      }
      // Don't gate the toast on response.data.status === 'success' — some
      // collaboration endpoints just return 200 with the updated record.
      // Falling through silently looks like the click did nothing.
      toast.success(
        response.data?.message ||
          (schedule.on_hold ? 'Hold released' : 'Schedule placed on hold'),
      );
      await fetchDetails();
      onUpdate?.();
    } catch (err: unknown) {
      console.error('Failed to toggle hold:', err);
      const axiosError = err as { response?: { data?: { message?: string; detail?: string } } };
      toast.error(
        axiosError.response?.data?.message ||
          axiosError.response?.data?.detail ||
          'Failed to update hold status',
      );
      throw err;
    }
  };

  const handleWorkflowAction = async (action: string, data: Record<string, unknown>) => {
    if (!schedule) return;
    const SUCCESS_FALLBACK: Record<string, string> = {
      accept: 'Delivery date accepted',
      decline: 'Delivery date declined',
      propose_date: 'Alternative date proposed',
      propose_qty: 'Quantity change proposed',
    };
    try {
      let response;
      switch (action) {
        case 'accept':
          response = await purchasingAPI.acceptScheduleDate(schedule.id, { comments: data['comments'] as string | undefined });
          break;
        case 'decline':
          response = await purchasingAPI.declineScheduleDate(schedule.id, { reason: data['reason'] as string, comments: data['comments'] as string | undefined });
          break;
        case 'propose_date':
          response = await purchasingAPI.proposeScheduleDate(schedule.id, { proposed_date: data['proposed_date'] as string, reason: data['reason'] as string, comments: data['comments'] as string | undefined });
          break;
        case 'propose_qty':
          response = await purchasingAPI.proposeScheduleQty(schedule.id, { proposed_quantity: data['proposed_quantity'] as number, reason: data['reason'] as string, comments: data['comments'] as string | undefined });
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }
      // Always toast on success — even if the backend doesn't return a
      // message field — so the user has explicit feedback. Previously a
      // missing message field meant the click looked like a no-op.
      toast.success(response?.data?.message || SUCCESS_FALLBACK[action] || 'Action completed');
      setActiveAction(null);
      await fetchDetails();
      onUpdate?.();
    } catch (err: unknown) {
      console.error('Workflow action failed:', err);
      const axiosError = err as { response?: { data?: { message?: string; detail?: string } } };
      const msg =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.detail ||
        (err instanceof Error ? err.message : 'Action failed');
      toast.error(msg);
      throw err;
    }
  };

  /** Whether accept/decline should be shown. */
  const canShowWorkflowActions = (): boolean => {
    if (!schedule || !user?.platform) return false;
    // Shipped, cancelled and missed are all past collaborating about: the
    // row's controls are hidden for the same reason (see COLLAB_CLOSED_STATUSES).
    if (isCollabClosed(schedule)) return false;
    const status = schedule.acknowledge_status;
    const isCustomer = user.platform === 'CUSTOMER' || user.platform === 'HELIX';
    const isSupplier = user.platform === 'SUPPLIER';
    if (isCustomer && status === 'change_proposed_supplier') return true;
    if (isCustomer && status === 'change_declined' && schedule.previous_acknowledge_status === 'change_proposed_supplier') return true;
    if (isSupplier && (status === 'new' || status === 'change_proposed_customer')) return true;
    if (isSupplier && status === 'change_declined' && schedule.previous_acknowledge_status === 'change_proposed_customer') return true;
    return false;
  };

  /** Whether propose date/qty should be shown. */
  const canShowProposeChange = (): boolean => {
    if (!schedule || !user?.platform) return false;
    if (isCollabClosed(schedule)) return false;
    const status = schedule.acknowledge_status;
    const isCustomer = user.platform === 'CUSTOMER' || user.platform === 'HELIX';
    const isSupplier = user.platform === 'SUPPLIER';
    if (isCustomer && ['new', 'accepted', 'wip'].includes(status || '')) return true;
    if (isCustomer && status === 'change_declined' && schedule.previous_acknowledge_status === 'change_proposed_customer') return true;
    if (isSupplier && ['new', 'accepted', 'wip'].includes(status || '')) return true;
    if (isSupplier && status === 'change_declined' && schedule.previous_acknowledge_status === 'change_proposed_supplier') return true;
    return false;
  };

  const ackStatus = schedule?.acknowledge_status || 'new';
  const ackBadge = ACK_BADGE[ackStatus] || { label: ackStatus, cls: 'bg-gray-100 text-gray-700' };
  const statusBadge = STATUS_BADGE[schedule?.status || ''] || { label: schedule?.status || '', cls: 'bg-gray-100 text-gray-700' };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-h-[95vh] flex flex-col" style={{ maxWidth: '1200px', minWidth: '900px' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b bg-muted/30">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-xl font-semibold text-primary">
                Schedule: {schedule?.po_number} / Line {schedule?.line_number} / Sched {schedule?.schedule_line_number || '-'}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Part: <span className="font-mono font-medium">{schedule?.part_number}</span>
                {schedule && <span className="ml-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ackBadge.cls}`}>{ackBadge.label}</span>
                </span>}
                {schedule?.wb_status && WB_BADGE[schedule.wb_status] && (
                  <span className="ml-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${WB_BADGE[schedule.wb_status].cls}`}>
                      {WB_BADGE[schedule.wb_status].label}
                    </span>
                  </span>
                )}
                {schedule?.otif_overridden && (
                  <span className="ml-2">
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 inline-flex items-center gap-1"
                      title={schedule.otif_override_reason ? `Manual override: ${schedule.otif_override_reason}` : 'Manual OTIF override'}
                    >
                      <ShieldCheck className="h-3 w-3" /> OTIF Passed (Override)
                    </span>
                  </span>
                )}
              </p>
            </div>
            {schedule && (() => {
              // Only the side that placed a hold may release it (customer
              // review 2026-07-08); backend enforces the same. Legacy holds
              // with no recorded placer stay releasable by either side.
              const viewerSide = user?.platform === 'SUPPLIER' ? 'supplier' : 'customer';
              const placedBy = schedule.on_hold_by_company_type;
              const canRelease = !placedBy || placedBy === viewerSide || user?.platform === 'HELIX';
              if (schedule.on_hold && !canRelease) {
                return (
                  <span
                    className="px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 bg-red-100 text-red-700 cursor-not-allowed"
                    title={`On hold by the ${placedBy} — only they can release it`}
                  >
                    <PauseCircle className="h-4 w-4" />
                    On Hold ({placedBy})
                  </span>
                );
              }
              return (
                <button
                  onClick={() => {
                    if (schedule.on_hold) handleToggleHold();
                    else setActiveAction('hold');
                  }}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors ${
                    schedule.on_hold
                      ? 'bg-red-500 text-white hover:bg-red-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  title={schedule.on_hold ? 'Click to release from hold' : 'Click to put on hold'}
                >
                  <PauseCircle className="h-4 w-4" />
                  {schedule.on_hold ? 'Take Off Hold' : 'Put On Hold'}
                </button>
              );
            })()}
            {/* Staff-only OTIF override — hidden for line-0 anchors and
                dateless rows (they carry no OTIF verdict; backend rejects). */}
            {schedule && canOtifOverride && schedule.schedule_line_number !== '0' && schedule.schedule_date && (
              <button
                onClick={() => setOtifOverrideOpen(true)}
                className={`px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors ${
                  schedule.otif_overridden
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                title={schedule.otif_overridden ? 'View / remove the manual OTIF override' : 'Mark this schedule OTIF-passed with a reason (staff only)'}
              >
                <ShieldCheck className="h-4 w-4" />
                {schedule.otif_overridden ? 'OTIF Overridden' : 'OTIF Override'}
              </button>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-full transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
              <p className="text-red-800">Error: {error}</p>
              <button onClick={() => fetchDetails()} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">Try again</button>
            </div>
          ) : schedule ? (
            <Tabs defaultValue="details" className="w-full">
              <div className="border-b px-4 pt-2">
                <TabsList className="bg-transparent border-none">
                  <TabsTrigger value="details" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">Details</TabsTrigger>
                  <TabsTrigger value="grns" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">
                    <span className="flex items-center gap-1"><Package className="h-4 w-4" />GRNs</span>
                  </TabsTrigger>
                  <TabsTrigger value="communication" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">
                    <span className="flex items-center gap-1"><FileText className="h-4 w-4" />Communication</span>
                  </TabsTrigger>
                  <TabsTrigger value="history" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">
                    <span className="flex items-center gap-1"><Clock className="h-4 w-4" />Change History ({changelogs.length})</span>
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="details" className="m-0 p-4">
                <ScheduleDetailFormContent
                  schedule={schedule}
                  changelogs={changelogs}
                  formatDate={formatDate}
                  statusBadge={statusBadge}
                  ackBadge={ackBadge}
                />
                {/* Workflow Actions */}
                <ScheduleDetailActions
                  schedule={schedule}
                  activeAction={activeAction}
                  setActiveAction={setActiveAction}
                  canShowWorkflowActions={canShowWorkflowActions()}
                  canShowProposeChange={canShowProposeChange()}
                  onWorkflowAction={handleWorkflowAction}
                  onToggleHold={handleToggleHold}
                />
              </TabsContent>

              <TabsContent value="grns" className="m-0 p-6">
                <ScheduleGRNTab
                  poLineId={schedule.po_line_id}
                  poNumber={schedule.po_number}
                  lineNumber={schedule.line_number}
                  scheduleId={schedule.id}
                  scheduleQty={parseFloat(schedule.schedule_qty || '0')}
                />
              </TabsContent>

              <TabsContent value="communication" className="m-0 p-6">
                <CommunicationLog
                  lineId={schedule.po_line_id}
                  poId={schedule.po_id}
                  partNumber={schedule.part_number}
                />
              </TabsContent>

              <TabsContent value="history" className="m-0 p-6">
                <Changelog entries={changelogs} emptyMessage="No change history for this schedule line" />
              </TabsContent>
            </Tabs>
          ) : null}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-3 bg-muted/10">
          <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg hover:bg-muted font-medium">Close</button>
        </div>
      </div>

      {/* Staff-only OTIF override dialog — refetch pulls the new verdict,
          badge and the audit entry into the Change History tab. */}
      <OtifOverrideDialog
        open={otifOverrideOpen}
        schedule={schedule}
        onClose={() => setOtifOverrideOpen(false)}
        onDone={() => { cacheBustRef.current = Date.now(); fetchDetails(); onUpdate?.(); }}
      />
    </div>
  );
}

// ScheduleDetailFormContent moved to its own file to keep this file under 500 lines.
